import LoadingButton from "@mui/lab/LoadingButton";
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, Stack } from "@mui/material";
import DialogContentText from "@mui/material/DialogContentText";
import { useRecordContext, useRefresh } from "react-admin";
import { FormProvider, useForm, useFormContext } from "react-hook-form";
import { useMutation, useQuery } from "react-query";
import { getAuthenticatedBackendClient } from "../../api/backend.api.js";
import { reportError } from "../../backoffice.utils";
import { useDialog } from "../../hooks/useDialog";
import { EurosInput } from "../../inputs/MoneyInput.js";
import { SelectInput } from "../../inputs/SelectInput.js";
import { DialogCloseButton } from "../../misc/DialogCloseButton";
import { DialogProps } from "../../misc/DialogProps";
import { formatMoney } from "../../misc/Money.js";
import { Row } from "../../misc/Row";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import { Student } from "../../providers/studentsProvider.js";

type PrepaymentFormValues = {
  amount: number | null;
  paymentTerminalId: string | null;
};

// Shows the PrepaidFormDialog and a ConfirmDialog afterwards
export const CardReaderPaymentDialogs = (
  props: {
    paymentTerminalId: string | null;
    drivingSchoolId: string;
    openDialog: () => void;
  } & DialogProps,
) => {
  const { open, onClose, openDialog, paymentTerminalId, drivingSchoolId } = props;
  const {
    isDialogOpen: isConfirmDialogOpen,
    openDialog: openConfirmDialog,
    closeDialog: closeConfirmDialog,
  } = useDialog();
  const form = useForm<PrepaymentFormValues>({
    defaultValues: {
      amount: 0,
      paymentTerminalId,
    },
  });

  return (
    <FormProvider {...form}>
      <PrepaidFormDialog
        open={open}
        onClose={onClose}
        onConfirm={openConfirmDialog}
        drivingSchoolId={drivingSchoolId}
      />
      <PrepaymentConfirmDialog
        open={isConfirmDialogOpen}
        onClose={() => {
          form.reset();
          closeConfirmDialog();
        }}
        onBack={() => {
          closeConfirmDialog();
          openDialog();
        }}
      />
    </FormProvider>
  );
};

type PrepaidFormDialogProps = {
  onConfirm: () => void;
  drivingSchoolId: string;
} & DialogProps;

const PrepaidFormDialog = ({ open, onClose, onConfirm, drivingSchoolId }: PrepaidFormDialogProps) => {
  const resetAndClose = () => {
    reset();
    onClose();
  };
  const { trigger, reset } = useFormContext<PrepaymentFormValues>();
  const {
    data: paymentTerminals,
    isLoading: arePaymentTerminalsLoading,
    isError: paymentTerminalsLoadingFailed,
  } = useQuery({
    queryKey: ["paymentTerminals", drivingSchoolId],
    queryFn: async () => {
      const backendClient = await getAuthenticatedBackendClient();
      return backendClient.stripe.stripeCardTerminalsList({
        companyDrivingSchoolId: drivingSchoolId,
        limit: 100,
      });
    },
    enabled: open,
  });
  const hasPaymentTerminals = !!paymentTerminals?.results.length;

  return (
    <Dialog open={open} onClose={resetAndClose} maxWidth="sm" fullWidth>
      <DialogTitle>Guthaben vor Ort einzahlen</DialogTitle>
      <DialogCloseButton onClick={resetAndClose} />
      <DialogContent>
        {hasPaymentTerminals ? (
          <PrepaymentForm
            paymentTerminalOptions={paymentTerminals.results.map((terminal): [string, string] => [
              terminal.id,
              terminal.display_name,
            ])}
          />
        ) : arePaymentTerminalsLoading ? (
          <DialogContentText>Kartenlesegeräte werden geladen.</DialogContentText>
        ) : paymentTerminalsLoadingFailed ? (
          <DialogContentText>Kartenlesegeräte konnten nicht geladen werden.</DialogContentText>
        ) : (
          <DialogContentText>Für diese Fahrschule ist kein Kartenlesegerät konfiguriert.</DialogContentText>
        )}
      </DialogContent>
      <DialogActions>
        {hasPaymentTerminals ? (
          <Button
            endIcon={<NavigateNextIcon />}
            variant="outlined"
            onClick={async () => {
              const valid = await trigger();
              if (!valid) return;
              onClose();
              onConfirm();
            }}
          >
            Weiter
          </Button>
        ) : (
          <Button variant="outlined" onClick={resetAndClose}>
            Schließen
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

function PrepaymentForm(props: { paymentTerminalOptions: Array<[string, string]> }) {
  const { paymentTerminalOptions } = props;
  const { setValue } = useFormContext();
  return (
    <Stack gap="5px">
      <DialogContentText>
        <ol style={{ paddingLeft: "16px" }}>
          <li>Bitte Betrag eingeben, den der Kunde vor Ort einzahlen möchte.</li>
          <li>Bitte sicherstellen, dass das Kartenlesegerät eingeschaltet ist und mit dem WLAN verbunden ist.</li>
          <li>Es kann einige Sekunden dauern, bis die Zahlung verarbeitet wird und auf dem Terminal angezeigt wird.</li>
        </ol>
      </DialogContentText>
      <FormControl>
        <Row sx={{ mt: 1.5, mb: 3 }} spacing={1}>
          <EurosInput
            label="Betrag"
            autoFocus
            source="amount"
            sx={{ "& .MuiInputBase-root": { minWidth: "150px", width: "150px" } }}
            validate={(value) => {
              if (value === null) {
                return "Betrag ist erforderlich";
              }
              if (value < 1) {
                return "Betrag muss mindestens 1 Euro sein";
              }
            }}
            onChange={(value) => setValue("amount", value)}
          />
        </Row>
      </FormControl>
      <SelectInput
        fullWidth
        source="paymentTerminalId"
        label="Kartenlesegerät"
        options={paymentTerminalOptions}
        required
        validate={(value) => (!value ? "Bitte wähle ein Kartenlesegerät aus." : undefined)}
      />
    </Stack>
  );
}

type PrepaymentConfirmDialogProps = {
  onBack: () => void;
} & DialogProps;

const PrepaymentConfirmDialog = ({ open, onClose, onBack }: PrepaymentConfirmDialogProps) => {
  const student = useRecordContext<Student>();
  const form = useFormContext<PrepaymentFormValues>();
  const refresh = useRefresh();

  const total = form.watch("amount");

  async function createPrepaidPaymentUsingTerminal(values: PrepaymentFormValues): Promise<void> {
    if (values.amount === null || values.amount < 1) {
      throw new Error(`Amount must be at least 1. ${JSON.stringify(values)}`);
    }
    if (!values.paymentTerminalId) {
      throw new Error("No payment terminal selected.");
    }
    const backendClient = await getAuthenticatedBackendClient();
    const order = await backendClient.order.orderPrepaidCardTerminalCreate({
      requestBody: {
        user: { id: student.autovioUserId },
        amount: { amount: values.amount.toString(), currency: "EUR" },
        driving_school_id: student.drivingSchoolId,
      },
    });
    if (!order.payment.id) {
      throw new Error(`No payment id found for order ${order.id}`);
    }
    await backendClient.payment.paymentProcessInTerminalCreate({
      id: order.payment.id,
      requestBody: { terminal_id: values.paymentTerminalId },
    });
  }

  const { mutate: executeMutation, isLoading } = useMutation("b2cInvoices", createPrepaidPaymentUsingTerminal, {
    onSuccess: () => {
      onClose();
      refresh();
      return showSuccessToast(`Zahlung wird erstellt. Dies kann ein wenig dauern.`);
    },
    onError: (error) => {
      reportError(`Failed to create invoice for student ${student.name} (${student.id})`, error);
      showErrorToast(`Zahlung konnte nicht erstellt werden.`);
    },
  });
  const onSubmit = (values: PrepaymentFormValues) => {
    executeMutation(values);
  };

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>Guthaben vor Ort einzahlen</DialogTitle>
      <DialogCloseButton onClick={onClose} disabled={isLoading} />
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <DialogContent>
          <Stack spacing="1em">
            {total && (
              <DialogContentText>
                Zahlung von <b>{formatMoney(total)}</b> durchführen und als Guthaben für <b>{student.name}</b>{" "}
                verwenden?
              </DialogContentText>
            )}
            <DialogContentText>
              Bitte sicherstellen, dass das Kartenlesegerät eingeschaltet ist und Netzverbindung hat.
            </DialogContentText>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button startIcon={<NavigateBeforeIcon />} variant="outlined" onClick={onBack} disabled={isLoading}>
            Zurück
          </Button>
          <LoadingButton
            loading={isLoading}
            loadingPosition="start"
            variant="contained"
            onClick={form.handleSubmit(onSubmit)}
          >
            Gerät bereit, jetzt bezahlen
          </LoadingButton>
        </DialogActions>
      </form>
    </Dialog>
  );
};
